import css from './app.module.less';
import { Swipeable } from './components/swipeable';
import ErrorBoundary from './error/errorBoundary.component';
import { useConfigureLocales } from './hooks/useConfigureLocales';
import { useDateFnsLocale } from './hooks/useDateFnsLocale';
import { useStoreGuid } from './lib/hooks';
import ForgotPassword from './login/forgotPassword';
import Login from './login/login.component';
import Navigation from './navigation/navigation';
import { toggleNavigation } from './navigation/navigation.action';
import { useAppDispatch, useAppSelector } from './redux';
import { Route as RouteDefinition, useAccessibleRoutes } from './routes';
import store, { persistor } from './store';
import { theme } from './theme';
import TitleBar from './titleBar/titleBar.component';
import UserActivation from './user/userActivation.component';
import { tableTheme } from './util/tableTheme';
import BuildMonitorStandalone from '@/buildMonitor/buildMonitorStandalone';
import commodityGroupService from '@/commodityGroup/cg.service';
import { useErrorMessage, useShowError } from '@/error/useMessage';
import useCurrentBrand from '@/hooks/useCurrentBrand';
import inventoryManagementSystemService from '@/inventorySystem/inventoryManagementSystem.service';
import storeService from '@/store/store.service';
import { useRedirectToStore } from '@/store/storeAutoSelect';
import TranslationContext from '@/translations/translationContext';
import { t, useTranslator } from '@/translations/translator';
import { FlatProviders } from '@/util/flatProviders';
import { DialogProvider } from '@/util/imperativeDialog';
import 'react-toastify/dist/ReactToastify.css';
import { StyledEngineProvider, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { BrandProvider } from '@whiz-cart/ui-shared/brand/brandContext.tsx';
import { StableComponent } from '@whiz-cart/ui-shared/components/stableComponent.tsx';
import { ImgSrcConfig, ImgSrcContext } from '@whiz-cart/ui-shared/img/imgSrc';
import LoadingIndicator from '@whiz-cart/ui-shared/loading/loadingIndicator.tsx';
import { OverlayContainer } from '@whiz-cart/ui-shared/overlay/overlayContainer.component.tsx';
import { UiSharedProvider } from '@whiz-cart/ui-shared/uiSharedConfig.js';
import { urlService } from '@whiz-cart/ui-shared/url/url.service';
import { useZodIssueReporting } from '@whiz-cart/ui-shared/zod/useZodIssueReporting';
import c from 'classnames';
import { SnackbarProvider } from 'notistack';
import { Suspense, useMemo } from 'react';
import { Provider } from 'react-redux';
import type { RouteChildrenProps } from 'react-router';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import { LEFT, RIGHT, SwipeEventData } from 'react-swipeable';
import { ToastContainer } from 'react-toastify';
import { PersistGate } from 'redux-persist/integration/react';
import { DatePickerProvider, TableSettingsProvider } from 'schummar-table';
import ExcelExporter from 'schummar-table/excelExporter';

function Main() {
    const dispatch = useAppDispatch();
    const navigationToggled = useAppSelector((state) => state.navigation);
    const navigationHidden = useAppSelector((state) => state.titleBar?.current?.hideNavigation);
    const stores = useAppSelector((state) => state.stores.stores);
    const url = useAppSelector((state) => state?.url);
    const { storeGuid } = useStoreGuid();
    const { isLoggedIn, accessibleRoutes, isLoading: isLoadingRoutes, isConditionalRoutesLoading } = useAccessibleRoutes();
    const fastLogin = url.hash.fastLogin;

    useConfigureLocales();

    function onSwiped({ dir, velocity, absX, event }: SwipeEventData) {
        // Open/hide navigation on swipe flick gesture
        const startX = ((event as TouchEvent).changedTouches?.[0]?.clientX ?? 0) - absX;
        if (startX < 50 && dir === RIGHT && velocity > 0.2 && !navigationToggled) {
            dispatch(toggleNavigation(true));
        } else if (dir === LEFT && velocity > 0.2 && navigationToggled) {
            dispatch(toggleNavigation(false));
        }
    }

    function renderRoute(route: RouteDefinition) {
        if (route.redirect) {
            return <Redirect key={route.path} exact={route.exact} path={route.path} to={urlService.calcUrl(route.redirect)} />;
        }

        return (
            <Route key={route.path} exact={route.exact} path={route.path}>
                {(props) => (
                    <>
                        {renderComponent(route, props)}

                        {route.nestedChildren?.length ? <Switch>{route.nestedChildren.map(renderRoute)}</Switch> : null}
                    </>
                )}
            </Route>
        );
    }

    function renderComponent(
        route: RouteDefinition,
        props: RouteChildrenProps<
            {
                [x: string]: string | undefined;
            },
            unknown
        >,
    ) {
        if (!route.component) {
            return null;
        }

        let content = (
            <Suspense fallback={<LoadingIndicator label={t('loadingModule')} showWithDelay={1000} />}>
                <StableComponent component={route.component} key={storeGuid} props={props} />
            </Suspense>
        );

        if (route.nested === 'fullpage') {
            content = (
                <div
                    css={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: 'var(--palette-background-paper)',
                        zIndex: 20,
                    }}
                >
                    {content}
                </div>
            );
        }

        return content;
    }

    useRedirectToStore(stores, storeGuid, url);

    const defaultPage = accessibleRoutes.find((x) => x.translationKey);
    const defaultPath = defaultPage?.link;

    return (
        <Router history={urlService.history as any}>
            <DialogProvider global>
                <Switch>
                    <Route path="/activate">
                        <UserActivation />
                    </Route>

                    <Route path="/forgotPassword">
                        <ForgotPassword />
                    </Route>

                    <Route path="/standalone/buildMonitor">
                        <BuildMonitorStandalone />
                    </Route>

                    {(isLoggedIn === false || fastLogin) && (
                        <Route>
                            <Login
                                fastLogin={fastLogin}
                                clearFastLogin={() => {
                                    urlService.replace({
                                        hash: { fastLogin: undefined },
                                    });
                                }}
                            />
                        </Route>
                    )}

                    {isLoggedIn === true && !isLoadingRoutes && (
                        <Route>
                            <Swipeable
                                testId="appMain"
                                className={c(css.main, { [css.navigationHidden]: !navigationToggled || navigationHidden })}
                                onSwiped={onSwiped}
                            >
                                {!isLoadingRoutes && <Navigation />}
                                <TitleBar />
                                <div data-testid="appContent" className={css.content}>
                                    <Suspense fallback={<LoadingIndicator label={t('loadingModule')} />}>
                                        <Switch>
                                            {accessibleRoutes.map(renderRoute)}
                                            {defaultPath && !isConditionalRoutesLoading && (
                                                <Redirect to={urlService.calcUrl(defaultPath)} />
                                            )}
                                        </Switch>
                                    </Suspense>
                                </div>
                            </Swipeable>
                        </Route>
                    )}
                </Switch>
            </DialogProvider>
        </Router>
    );
}

export default function App() {
    useZodIssueReporting();

    const { storeGuid } = useStoreGuid();
    const [storeConfig] = storeService.getConfig.useAction(storeGuid, { dormant: !storeGuid });
    const brand = useCurrentBrand();

    const imgSrcConfig = useMemo(
        (): ImgSrcConfig => ({
            url: config?.cdnUrl,
            token: config?.cdnToken,
            brandImageUrl: config?.brandImageUrl,
            snapshotImageUrl: config?.snapshotImageUrl,
            supportInfoUrl: config?.supportInfoUrl,
            promotionsUrl: config?.promotionsUrl,
            pluHierarchyUrl: config?.pluHierarchyUrl,
            infoMaterialsUrl: config?.infoMaterialsUrl,
            storeGuid,
        }),
        [
            // oxlint-disable exhaustive-deps
            config?.cdnUrl,
            config?.cdnToken,
            config?.brandImageUrl,
            config?.snapshotImageUrl,
            config?.supportInfoUrl,
            config?.promotionsUrl,
            config?.pluHierarchyUrl,
            config?.infoMaterialsUrl,
            // oxlint-enable exhaustive-deps
            storeGuid,
        ],
    );

    // eslint-disable jsx-key
    const providers = [
        <StyledEngineProvider injectFirst />,
        <ThemeProvider theme={theme} />,
        // @ts-expect-error missing children
        <Provider store={store} />,
        <ImgSrcContext.Provider value={imgSrcConfig} />,
        <BrandProvider brand={brand} />,
        <TranslationContext />,
        <UiSharedProvider
            storeGuid={storeGuid}
            currency={storeConfig?.uiDisplayCurrency ?? undefined}
            errorHook={useErrorMessage}
            errorHandlerHook={useShowError}
            commodityGroupService={commodityGroupService}
            inventoryManagementSystemService={inventoryManagementSystemService}
        />,
        <TableSettingsProvider theme={tableTheme} additionalExporters={[{ action: 'download', exporter: new ExcelExporter() }]} />,
        <ErrorBoundary />,
        <SnackbarProvider maxSnack={4} hideIconVariant anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }} preventDuplicate>
            {null}
        </SnackbarProvider>,
        <PersistGate loading={null} persistor={persistor} />,
    ];
    // eslint-enable jsx-key

    return (
        <FlatProviders providers={providers}>
            <AppInner />
        </FlatProviders>
    );
}

function AppInner() {
    const t = useTranslator();
    const dateAdapterLocale = useDateFnsLocale(t.locale);

    return (
        <DatePickerProvider showCalendarWeek locale={t.locale}>
            <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={dateAdapterLocale}
                localeText={{
                    cancelButtonLabel: t('cancel'),
                    clearButtonLabel: t('clear'),
                    okButtonLabel: t('ok'),
                }}
            >
                <ToastContainer
                    position="bottom-center"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={true}
                    closeOnClick
                    rtl={false}
                    draggable
                    pauseOnHover
                />
                <Main />
                <OverlayContainer />
            </LocalizationProvider>
        </DatePickerProvider>
    );
}
